import {
  Alert,
  Button,
  Chip,
  Input,
  Option,
  Select,
  Textarea,
  Typography,
} from "@mui/joy";
import { Box, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useGetOrganizationsQuery } from "@nerdjs/account-kit";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  NOTICE_PERIOD_DAYS,
  TOS_KINDS,
  TosAudience,
  TosKind,
  TosMode,
  TosVersion_Entity,
  daysUntilEffective,
} from "../../entities/tosVersion";
import {
  formOpenSelector,
  selectedTosVersionIDSelector,
  setFormOpen,
  setSelectedTosVersionID,
} from "../../reducers/rhapsody";
import {
  useCreateTosVersionMutation,
  useUpdateTosVersionMutation,
} from "../../redux/tosVersion/tosVersionEndpoints";
import { useTosVersions } from "../../redux/tosVersion/tosVersionHooks";
import DialogClose from "../dialogClose/dialogClose";

const DEFAULT_URLS: Record<TosKind, string> = {
  terms: "https://legal.rhapsody.la/",
  privacy: "https://legal.rhapsody.la/privacy-policy",
  payroll: "https://legal.rhapsody.la/rhapsody-payroll-services-addendum",
  musician: "https://legal.rhapsody.la/musician-terms-of-use",
};

/**
 * Today + the notice period, so the safe thing is what happens when nobody
 * thinks about it.
 */
function defaultEffective(): string {
  const d = new Date();
  d.setDate(d.getDate() + NOTICE_PERIOD_DAYS);
  return d.toISOString().slice(0, 10);
}

/**
 *
 */
function blank(): Partial<TosVersion_Entity> {
  return {
    kind: "terms",
    version: "",
    url: DEFAULT_URLS.terms,
    content: "",
    summary: "",
    effectiveAt: defaultEffective(),
    mode: "soft",
    requiresAcceptance: true,
    audience: "all",
    organizationIDs: [],
    active: true,
  };
}

/**
 * Publish or edit a legal document version.
 *
 * Create and edit share the form because the fields interact and a grid cell
 * cannot express that: choosing Privacy Policy turns acceptance off (a notice
 * describing what we do with data has nothing to agree to), and choosing a
 * scoped audience needs an organization list to mean anything.
 *
 * There is no delete, here or in rhapsody-service. A published version is what
 * organizations have acknowledged, and deleting it would orphan that evidence.
 * Retire a version with Active instead.
 *
 * @returns {ReactElement} TosVersionForm page
 */
export function TosVersionForm() {
  const open = useSelector(formOpenSelector("tosVersion"));
  const selectedID = useSelector(selectedTosVersionIDSelector);
  const dispatch = useDispatch();
  const { tosVersions } = useTosVersions();
  const { data: orgData } = useGetOrganizationsQuery();
  const [createTosVersion, { isLoading: creating }] =
    useCreateTosVersionMutation();
  const [updateTosVersion, { isLoading: updating }] =
    useUpdateTosVersionMutation();
  const [_tosVersion, setTosVersion] = useState<Partial<TosVersion_Entity>>(
    blank()
  );
  const [preview, setPreview] = useState(false);

  const existing = tosVersions?.find((v) => v.id === selectedID);

  // A saved version may already have been acknowledged, in which case the server
  // rejects a text change. Warn on any saved version rather than querying for
  // acknowledgements here — a hint, with the service as the enforcement.
  const frozen = Boolean(_tosVersion.id);

  useEffect(() => {
    if (!open) return;
    if (existing?.id) {
      setTosVersion({
        ...existing,
        organizationIDs: existing.organizationIDs ?? [],
      });
    } else if (!selectedID) {
      setTosVersion(blank());
    }
  }, [open, selectedID, existing]);

  const days = daysUntilEffective(_tosVersion.effectiveAt);
  const short = days !== undefined && days < NOTICE_PERIOD_DAYS;
  const scoped = _tosVersion.audience === "organizations";
  const noOrgs = scoped && (_tosVersion.organizationIDs ?? []).length === 0;
  const valid = Boolean(_tosVersion.version?.trim() && _tosVersion.url?.trim());
  const kindMeta = TOS_KINDS.find((k) => k.value === _tosVersion.kind);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      setTosVersion((p) => ({
        ...p,
        content: text,
        // The filename is usually the slug, not the version, so only the text is
        // taken — the version string stays whatever was typed.
      }));
      setPreview(true);
    };
    reader.readAsText(file);
    // Allow re-selecting the same file after an edit.
    e.target.value = "";
  };

  const close = () => {
    dispatch(setFormOpen({ isOpen: false, formID: "tosVersion" }));
    dispatch(setSelectedTosVersionID(null));
    setTosVersion(blank());
    setPreview(false);
  };

  const save = () => {
    if (!valid) return;
    if (_tosVersion.id) {
      updateTosVersion({ id: _tosVersion.id, body: _tosVersion });
    } else {
      createTosVersion(_tosVersion);
    }
    close();
  };

  // Choosing a document also chooses whether it is a contract and who it applies
  // to. Both stay editable afterwards — a sensible starting point, not a lock.
  const onKindChange = (kind: TosKind) => {
    const meta = TOS_KINDS.find((k) => k.value === kind);
    setTosVersion((p) => ({
      ...p,
      kind,
      url: DEFAULT_URLS[kind],
      requiresAcceptance: meta?.requiresAcceptance ?? true,
      // The musician document has no meaning for organizations, so picking it
      // switches the population rather than leaving somebody to notice. Still
      // editable afterwards — a starting point, not a lock.
      audience:
        kind === "musician"
          ? "musicians"
          : kind === "payroll"
          ? "organizations"
          : "all",
    }));
  };

  return (
    <DialogClose open={open} onClose={close} maxWidth="sm">
      <DialogTitle>
        <Typography
          level="title-lg"
          startDecorator={<i className="fa-solid fa-file-contract"></i>}
        >
          {_tosVersion.id
            ? `${kindMeta?.label ?? "Document"} ${_tosVersion.version ?? ""}`
            : "New legal document version"}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", gap: 1.5, flexDirection: "column" }}
      >
        <Box>
          <Select
            slotProps={{ listbox: { sx: { zIndex: 9999 } } }}
            value={_tosVersion.kind ?? "terms"}
            onChange={(e, v) => onKindChange((v as TosKind) ?? "terms")}
            startDecorator="Document:"
          >
            {TOS_KINDS.map((k) => (
              <Option key={k.value} value={k.value}>
                {k.label}
              </Option>
            ))}
          </Select>
          {kindMeta ? (
            <Typography level="body-xs" sx={{ mt: 0.5 }}>
              {kindMeta.hint}
            </Typography>
          ) : null}
        </Box>

        <Box>
          <Input
            value={_tosVersion.version ?? ""}
            onChange={(e) =>
              setTosVersion((p) => ({ ...p, version: e.target.value }))
            }
            autoFocus
            startDecorator="Version:"
            placeholder="2026-07-29"
            sx={{ boxShadow: "none" }}
          />
          <Typography level="body-xs" sx={{ mt: 0.5 }}>
            Match the &quot;Last updated&quot; date on the published document.
          </Typography>
        </Box>

        <Input
          value={_tosVersion.url ?? ""}
          onChange={(e) =>
            setTosVersion((p) => ({ ...p, url: e.target.value }))
          }
          startDecorator="URL:"
          sx={{ boxShadow: "none" }}
        />

        <Input
          type="date"
          value={(_tosVersion.effectiveAt ?? "").slice(0, 10)}
          onChange={(e) =>
            setTosVersion((p) => ({ ...p, effectiveAt: e.target.value }))
          }
          startDecorator="Effective from:"
          sx={{ boxShadow: "none" }}
        />

        <Select
          slotProps={{ listbox: { sx: { zIndex: 9999 } } }}
          value={_tosVersion.requiresAcceptance === false ? 0 : 1}
          onChange={(e, v) =>
            setTosVersion((p) => ({ ...p, requiresAcceptance: v === 1 }))
          }
          startDecorator="Acceptance:"
        >
          <Option value={1}>Required — organizations must accept</Option>
          <Option value={0}>Notice only — nothing to accept</Option>
        </Select>

        {_tosVersion.requiresAcceptance === false ? (
          <Typography level="body-xs">
            Organizations will be shown this and the sighting recorded, but
            never asked to agree. Mode is ignored for notice-only documents.
          </Typography>
        ) : (
          <Select
            slotProps={{ listbox: { sx: { zIndex: 9999 } } }}
            value={_tosVersion.mode ?? "soft"}
            onChange={(e, v) =>
              setTosVersion((p) => ({ ...p, mode: (v as TosMode) ?? "soft" }))
            }
            startDecorator="Mode:"
          >
            <Option value="soft">Soft — dismissible notice</Option>
            <Option value="blocking">Blocking — must acknowledge</Option>
          </Select>
        )}

        <Select
          slotProps={{ listbox: { sx: { zIndex: 9999 } } }}
          value={_tosVersion.audience ?? "all"}
          onChange={(e, v) =>
            setTosVersion((p) => ({
              ...p,
              audience: (v as TosAudience) ?? "all",
            }))
          }
          startDecorator="Applies to:"
        >
          <Option value="all">All organizations</Option>
          <Option value="organizations">Selected organizations</Option>
          <Option value="musicians">Musicians (local420)</Option>
        </Select>

        {scoped ? (
          <Box>
            <Select
              multiple
              slotProps={{ listbox: { sx: { zIndex: 9999 } } }}
              value={_tosVersion.organizationIDs ?? []}
              onChange={(e, v) =>
                setTosVersion((p) => ({
                  ...p,
                  organizationIDs: (v as number[]) ?? [],
                }))
              }
              startDecorator="Organizations:"
              renderValue={(selected) => (
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {selected.map((s) => (
                    <Chip key={s.value} size="sm" variant="soft">
                      {s.label}
                    </Chip>
                  ))}
                </Box>
              )}
            >
              {(orgData?.ids ?? []).map((id) => (
                <Option key={id} value={orgData?.entities[id]?.id}>
                  {orgData?.entities[id]?.name}
                </Option>
              ))}
            </Select>
            {noOrgs ? (
              <Alert color="warning" sx={{ mt: 1 }}>
                No organizations selected, so nobody will be shown this
                document.
              </Alert>
            ) : null}
          </Box>
        ) : null}

        <Box>
          <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5 }}>
            Document text
          </Typography>
          <Typography level="body-xs" sx={{ mb: 1 }}>
            Upload the Markdown file from the gitbook-legal repo. The text is
            stored with this version, so what a customer accepted stays readable
            years later even after the published page moves on.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
            <Button
              component="label"
              size="sm"
              variant="soft"
              startDecorator={<i className="fa-regular fa-file-arrow-up"></i>}
            >
              Upload .md
              <input
                type="file"
                accept=".md,.markdown,text/markdown,text/plain"
                hidden
                onChange={onFile}
              />
            </Button>
            {_tosVersion.content ? (
              <Chip size="sm" variant="soft" color="success">
                {Math.round((_tosVersion.content?.length ?? 0) / 1024)} KB
              </Chip>
            ) : (
              <Chip size="sm" variant="soft" color="warning">
                no text yet
              </Chip>
            )}
            {_tosVersion.content ? (
              <Button
                size="sm"
                variant="plain"
                color="neutral"
                onClick={() => setPreview((p) => !p)}
              >
                {preview ? "Hide source" : "View source"}
              </Button>
            ) : null}
          </Box>
          <Textarea
            value={_tosVersion.content ?? ""}
            onChange={(e) =>
              setTosVersion((p) => ({ ...p, content: e.target.value }))
            }
            minRows={preview ? 2 : 6}
            maxRows={preview ? 2 : 14}
            placeholder="# TERMS AND CONDITIONS&#10;&#10;Paste or upload the Markdown here."
          />
          {preview ? (
            <Box
              component="pre"
              sx={{
                mt: 1,
                p: 1.5,
                m: 0,
                maxHeight: 320,
                overflow: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "8px",
                fontSize: 12,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {_tosVersion.content ?? ""}
            </Box>
          ) : null}
          {frozen ? (
            <Alert color="neutral" sx={{ mt: 1 }}>
              This version has already been acknowledged, so its text is frozen
              — editing it would rewrite what those organizations agreed to.
              Publish a new version instead.
            </Alert>
          ) : null}
        </Box>

        <Textarea
          value={_tosVersion.summary ?? ""}
          onChange={(e) =>
            setTosVersion((p) => ({ ...p, summary: e.target.value }))
          }
          minRows={2}
          placeholder="What changed, in one line. Shown to customers."
        />

        <Select
          slotProps={{ listbox: { sx: { zIndex: 9999 } } }}
          value={_tosVersion.active ? 1 : 0}
          onChange={(e, v) => setTosVersion((p) => ({ ...p, active: v === 1 }))}
          startDecorator="Active:"
        >
          <Option value={1}>Yes</Option>
          <Option value={0}>No — retired</Option>
        </Select>

        {short && _tosVersion.requiresAcceptance !== false ? (
          <Alert color="warning">
            That is {days} day{days === 1 ? "" : "s"} away. Our Terms promise{" "}
            {NOTICE_PERIOD_DAYS} days&apos; notice before a change binds an
            existing customer. Publish sooner only if the change is urgent.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button variant="soft" color="neutral" onClick={close}>
          Cancel
        </Button>
        <Button onClick={save} loading={creating || updating} disabled={!valid}>
          {_tosVersion.id ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </DialogClose>
  );
}
